import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";

type Cfg = { base: string; roleKey: "venue_role" | "admin_role"; roles: string[]; prefix: string };

export const RoleSelect = ({ value, roles, onChange, testId }: { value: string; roles: string[]; onChange: (r: string) => void; testId: string }) => {
  const { t } = usePrefs();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="h-9 rounded-full border bg-background px-3 text-xs font-bold">
      {roles.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
    </select>
  );
};

export const TeamManager = ({ cfg, canManage }: { cfg: Cfg; canManage: boolean }) => {
  const { t } = usePrefs();
  const me = useAuth().user as User;
  const byUser = cfg.prefix === "venue";
  const empty = { name: "", email: "", username: "", password: "", role: cfg.roles[1] };
  const [list, setList] = useState<User[]>([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState<Record<string, string>>(empty);
  const load = () => api.get(cfg.base).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.base]);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      toast.success(t(msg));
      load();
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };
  const add = (e: React.FormEvent) => {
    e.preventDefault();
    act(async () => {
      await api.post(cfg.base, { name: f.name, ...(byUser ? { username: f.username.trim().toLowerCase() } : { email: f.email }), password: f.password, [cfg.roleKey]: f.role });
      setF(empty);
      setOpen(false);
    }, "team.added");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground max-w-lg">{t(cfg.prefix === "admin" ? "admin.teamSub" : "team.roleHint")}</p>
        {canManage && (
          <button data-testid={`${cfg.prefix}-add-member-button`} onClick={() => setOpen(true)} className="h-11 px-5 rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center gap-2">
            <UserPlus className="h-4 w-4" />{t("team.add")}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {list.map((u) => {
          const role = (u[cfg.roleKey] as string) || cfg.roles[0];
          const self = u.user_id === me.user_id;
          return (
            <div key={u.user_id} data-testid={`${cfg.prefix}-member-${u.user_id}`} className="min-w-0 rounded-3xl border bg-card p-4 flex items-center gap-3">
              <div className="h-12 w-12 shrink-0 rounded-2xl bg-secondary/20 grid place-items-center font-display font-black text-xl">{u.name[0]}</div>
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{u.name} {self && <span className="text-xs text-muted-foreground">({t("team.you")})</span>}</p>
                <p className="text-sm text-muted-foreground truncate" dir="ltr">{byUser ? `@${u.username || ""}` : u.email}</p>
              </div>
              {canManage && !self ? (
                <RoleSelect value={role} roles={cfg.roles} testId={`${cfg.prefix}-role-select-${u.user_id}`}
                  onChange={(r) => act(() => api.patch(`${cfg.base}/${u.user_id}`, { role: r }), "team.roleChanged")} />
              ) : (
                <span className="rounded-full bg-accent px-3 py-1 text-xs font-bold">{t(`role.${role}`)}</span>
              )}
              {canManage && !self && (
                <button data-testid={`${cfg.prefix}-remove-member-${u.user_id}`} onClick={() => window.confirm(`${t("team.remove")}: ${u.name}?`) && act(() => api.delete(`${cfg.base}/${u.user_id}`), "team.removed")}
                  aria-label={t("team.remove")} className="h-10 w-10 shrink-0 grid place-items-center rounded-full hover:bg-primary/10 text-primary">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid={`${cfg.prefix}-add-member-dialog`}>
          <DialogHeader className="text-start"><DialogTitle className="font-display text-xl">{t("team.add")}</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-3">
            {[["name", "auth.fullName", "text"], byUser ? ["username", "auth.username", "text"] : ["email", "auth.email", "email"], ["password", "auth.password", "password"]].map(([k, label, type]) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`m-${k}`}>{t(label)}</Label>
                <Input id={`m-${k}`} type={type} required minLength={k === "password" ? 8 : k === "username" ? 3 : 2} autoCapitalize={k === "username" ? "none" : undefined} dir={k === "name" ? undefined : "ltr"} value={f[k]}
                  onChange={(e: any) => setF({ ...f, [k]: e.target.value })} data-testid={`${cfg.prefix}-member-${k}-input`} className="h-11 rounded-2xl" />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>{t("team.role")}</Label>
              <RoleSelect value={f.role} roles={cfg.roles} onChange={(r) => setF({ ...f, role: r })} testId={`${cfg.prefix}-member-role-select`} />
            </div>
            <button data-testid={`${cfg.prefix}-add-member-submit`} className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold">{t("common.save")}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
